import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Shield, 
  Search, 
  Truck, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  Package,
  RefreshCw,
  LogOut,
  Home,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Read location from localStorage synchronously at module load
const getInitialLocation = () => {
  try {
    const storedCheckin = localStorage.getItem('security_checkin');
    if (storedCheckin) {
      const checkinData = JSON.parse(storedCheckin);
      return {
        locationId: checkinData.unit_location_id,
        locationName: checkinData.unit_location_name,
        securityName: checkinData.security_person_name
      };
    }
  } catch (e) {
    console.error('Error reading security checkin from localStorage:', e);
  }
  return { locationId: null, locationName: null, securityName: null };
};

export default function SecurityGatePassPage() {
  // Initialize location synchronously from localStorage
  const initialLocation = getInitialLocation();
  
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingPasses, setPendingPasses] = useState([]);
  const [pendingInPasses, setPendingInPasses] = useState([]);
  const [inTransitPasses, setInTransitPasses] = useState([]);  // New: In Transit
  const [todayOutPasses, setTodayOutPasses] = useState([]);
  const [todayInPasses, setTodayInPasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchNumber, setSearchNumber] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [checkedInSecurity, setCheckedInSecurity] = useState(
    initialLocation.securityName ? { name: initialLocation.securityName } : null
  );
  const [currentLocationId, setCurrentLocationId] = useState(initialLocation.locationId);
  const [currentLocationName, setCurrentLocationName] = useState(initialLocation.locationName);
  
  // Mark OUT dialog
  const [markOutOpen, setMarkOutOpen] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [markOutForm, setMarkOutForm] = useState({
    vehicle_number: '',
    driver_name: '',
    driver_contact: '',
    security_name: '',
    remarks: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Mark IN dialog (for IUT destination)
  const [markInOpen, setMarkInOpen] = useState(false);
  const [selectedInPass, setSelectedInPass] = useState(null);
  const [markInForm, setMarkInForm] = useState({
    security_name: '',
    remarks: ''
  });
  
  // Items View Dialog (Format C)
  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [itemsDialogData, setItemsDialogData] = useState(null);
  
  // Edit In Transit Dialog
  const [editTransitOpen, setEditTransitOpen] = useState(false);
  const [editTransitPass, setEditTransitPass] = useState(null);
  const [editTransitForm, setEditTransitForm] = useState({
    vehicle_number: '',
    driver_name: '',
    driver_contact: '',
    remarks: '',
    security_name: ''
  });
  
  // Reset (Delete) Confirmation Dialog
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetPass, setResetPass] = useState(null);
  const [resetReason, setResetReason] = useState('');

  // Fetch today's checked-in security person and get location
  const fetchCheckedInSecurity = useCallback(async () => {
    try {
      // First check localStorage for security check-in
      const storedCheckin = localStorage.getItem('security_checkin');
      if (storedCheckin) {
        const checkinData = JSON.parse(storedCheckin);
        setCurrentLocationId(checkinData.unit_location_id);
        setCurrentLocationName(checkinData.unit_location_name);
        setCheckedInSecurity({ name: checkinData.security_person_name });
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/public/security/today-checkin`);
      const persons = response.data.security_persons || [];
      // Get the first on-duty security person, or most recent if none on duty
      const onDuty = persons.find(p => p.is_on_duty);
      setCheckedInSecurity(onDuty || persons[0] || null);
    } catch (error) {
      console.error('Failed to fetch checked-in security:', error);
    }
  }, []);

  const fetchPendingPasses = useCallback(async () => {
    try {
      setLoading(true);
      // Filter by location if available
      let url = `${API_URL}/api/public/security/gate-pass/pending`;
      if (currentLocationId) {
        url += `?location_id=${currentLocationId}`;
      }
      const response = await axios.get(url);
      setPendingPasses(response.data.gate_passes || []);
    } catch (error) {
      console.error('Failed to fetch pending passes:', error);
      toast.error('Failed to load pending gate passes');
    } finally {
      setLoading(false);
    }
  }, [currentLocationId]);

  const fetchTodayOutPasses = useCallback(async () => {
    try {
      // Filter by location if available
      let url = `${API_URL}/api/public/security/gate-pass/today-out`;
      if (currentLocationId) {
        url += `?location_id=${currentLocationId}`;
      }
      const response = await axios.get(url);
      setTodayOutPasses(response.data.gate_passes || []);
    } catch (error) {
      console.error('Failed to fetch today\'s OUT passes:', error);
    }
  }, [currentLocationId]);

  // Fetch Pending IN passes (IUT awaiting Gate IN at this location)
  const fetchPendingInPasses = useCallback(async () => {
    try {
      let url = `${API_URL}/api/public/security/gate-pass/pending-in`;
      if (currentLocationId) {
        url += `?location_id=${currentLocationId}`;
      }
      const response = await axios.get(url);
      setPendingInPasses(response.data.gate_passes || []);
    } catch (error) {
      console.error('Failed to fetch pending IN passes:', error);
    }
  }, [currentLocationId]);

  // Fetch Today's IN passes
  const fetchTodayInPasses = useCallback(async () => {
    try {
      let url = `${API_URL}/api/public/security/gate-pass/today-in`;
      if (currentLocationId) {
        url += `?location_id=${currentLocationId}`;
      }
      const response = await axios.get(url);
      setTodayInPasses(response.data.gate_passes || []);
    } catch (error) {
      console.error('Failed to fetch today\'s IN passes:', error);
    }
  }, [currentLocationId]);

  // Fetch In Transit passes (sent from this location, awaiting Gate IN at destination)
  const fetchInTransitPasses = useCallback(async () => {
    try {
      let url = `${API_URL}/api/public/security/gate-pass/in-transit`;
      if (currentLocationId) {
        url += `?location_id=${currentLocationId}`;
      }
      const response = await axios.get(url);
      setInTransitPasses(response.data.gate_passes || []);
    } catch (error) {
      console.error('Failed to fetch in-transit passes:', error);
    }
  }, [currentLocationId]);

  useEffect(() => {
    // Only fetch from API if location wasn't found in localStorage
    if (!currentLocationId) {
      fetchCheckedInSecurity();
    }
  }, [fetchCheckedInSecurity, currentLocationId]);

  useEffect(() => {
    // Fetch all data - location filter is applied in each fetch function
    fetchPendingPasses();
    fetchPendingInPasses();
    fetchInTransitPasses();
    fetchTodayOutPasses();
    fetchTodayInPasses();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchPendingPasses();
      fetchPendingInPasses();
      fetchInTransitPasses();
      fetchTodayOutPasses();
      fetchTodayInPasses();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchPendingPasses, fetchPendingInPasses, fetchInTransitPasses, fetchTodayOutPasses, fetchTodayInPasses]);

  const handleLookup = async () => {
    if (!searchNumber.trim()) {
      toast.error('Please enter a Gate Pass number');
      return;
    }
    
    try {
      setLookupLoading(true);
      const response = await axios.get(`${API_URL}/api/public/security/gate-pass/lookup/${searchNumber.trim().toUpperCase()}`);
      setLookupResult(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Gate Pass not found');
      } else {
        toast.error('Failed to lookup Gate Pass');
      }
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const openMarkOutDialog = (gatePass) => {
    setSelectedPass(gatePass);
    // Auto-fill security name from today's check-in or localStorage
    const securityName = checkedInSecurity?.name || localStorage.getItem('security_name') || '';
    setMarkOutForm({
      vehicle_number: gatePass.vehicle_number || '',
      driver_name: gatePass.driver_name || '',
      driver_contact: '',
      security_name: securityName,
      remarks: ''
    });
    setMarkOutOpen(true);
  };

  const handleMarkOut = async () => {
    if (!markOutForm.vehicle_number.trim()) {
      toast.error('Vehicle number is required');
      return;
    }
    if (!markOutForm.security_name.trim()) {
      toast.error('Your name is required');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Save security name for future use
      localStorage.setItem('security_name', markOutForm.security_name);
      
      const response = await axios.post(`${API_URL}/api/public/security/gate-pass/mark-out`, {
        gate_pass_number: selectedPass.gate_pass_number,
        vehicle_number: markOutForm.vehicle_number.trim().toUpperCase(),
        driver_name: markOutForm.driver_name.trim() || null,
        driver_contact: markOutForm.driver_contact.trim() || null,
        security_name: markOutForm.security_name.trim(),
        remarks: markOutForm.remarks.trim() || null
      });
      
      toast.success(response.data.message);
      setMarkOutOpen(false);
      setLookupResult(null);
      setSearchNumber('');
      fetchPendingPasses();
      fetchTodayOutPasses();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to mark Gate Pass as OUT');
    } finally {
      setSubmitting(false);
    }
  };

  // Mark IN handlers (for IUT destination)
  const openMarkInDialog = (gatePass) => {
    setSelectedInPass(gatePass);
    const securityName = checkedInSecurity?.name || localStorage.getItem('security_name') || '';
    setMarkInForm({
      security_name: securityName,
      remarks: ''
    });
    setMarkInOpen(true);
  };

  const handleMarkIn = async () => {
    if (!markInForm.security_name.trim()) {
      toast.error('Your name is required');
      return;
    }
    
    try {
      setSubmitting(true);
      localStorage.setItem('security_name', markInForm.security_name);
      
      const response = await axios.post(`${API_URL}/api/public/security/gate-pass/mark-in`, {
        gate_pass_number: selectedInPass.gate_pass_number,
        security_name: markInForm.security_name.trim(),
        remarks: markInForm.remarks.trim() || null
      });
      
      toast.success(response.data.message);
      setMarkInOpen(false);
      fetchPendingInPasses();
      fetchTodayInPasses();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to mark Gate Pass as IN');
    } finally {
      setSubmitting(false);
    }
  };

  // Items Dialog handlers (Format C)
  const openItemsDialog = (gatePass) => {
    setItemsDialogData(gatePass);
    setItemsDialogOpen(true);
  };

  // Edit In Transit handlers
  const openEditTransitDialog = (gatePass) => {
    setEditTransitPass(gatePass);
    const securityName = checkedInSecurity?.name || localStorage.getItem('security_name') || '';
    setEditTransitForm({
      vehicle_number: gatePass.vehicle_number || '',
      driver_name: gatePass.driver_name || '',
      driver_contact: gatePass.driver_contact || '',
      remarks: gatePass.out_remarks || '',
      security_name: securityName
    });
    setEditTransitOpen(true);
  };

  const handleEditTransit = async () => {
    if (!editTransitForm.security_name.trim()) {
      toast.error('Your name is required');
      return;
    }
    
    try {
      setSubmitting(true);
      localStorage.setItem('security_name', editTransitForm.security_name);
      
      const response = await axios.patch(`${API_URL}/api/public/security/gate-pass/in-transit/update`, {
        gate_pass_number: editTransitPass.gate_pass_number,
        vehicle_number: editTransitForm.vehicle_number.trim() || null,
        driver_name: editTransitForm.driver_name.trim() || null,
        driver_contact: editTransitForm.driver_contact.trim() || null,
        remarks: editTransitForm.remarks.trim() || null,
        security_name: editTransitForm.security_name.trim()
      });
      
      toast.success(response.data.message);
      setEditTransitOpen(false);
      fetchInTransitPasses();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update Gate Pass');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset (Delete) handlers
  const openResetConfirm = (gatePass) => {
    setResetPass(gatePass);
    setResetReason('');
    setResetConfirmOpen(true);
  };

  const handleResetTransit = async () => {
    const securityName = checkedInSecurity?.name || localStorage.getItem('security_name') || '';
    if (!securityName) {
      toast.error('Security name is required');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await axios.post(`${API_URL}/api/public/security/gate-pass/in-transit/reset`, {
        gate_pass_number: resetPass.gate_pass_number,
        security_name: securityName,
        reason: resetReason.trim() || null
      });
      
      toast.success(response.data.message);
      setResetConfirmOpen(false);
      fetchInTransitPasses();
      fetchPendingPasses();  // Refresh pending as it will appear there
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to reset Gate Pass');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-900 text-white p-4 shadow-lg">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Security Gate Pass</h1>
              <p className="text-blue-200 text-sm">
                {currentLocationName ? `${currentLocationName} - Mark materials OUT` : 'Mark materials OUT at gate'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { fetchPendingPasses(); fetchPendingInPasses(); fetchInTransitPasses(); fetchTodayOutPasses(); fetchTodayInPasses(); }}
              className="text-white border-white hover:bg-blue-800"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.location.href = '/security-checkin'}
              className="text-white border-white hover:bg-blue-800"
              data-testid="home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Quick Lookup */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Quick Lookup
            </CardTitle>
            <CardDescription>Enter Gate Pass number to verify and mark OUT</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                placeholder="Enter Gate Pass Number (e.g., GP-MET-2602-001)"
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                className="flex-1 text-lg uppercase"
                data-testid="gp-search-input"
              />
              <Button onClick={handleLookup} disabled={lookupLoading} data-testid="gp-search-btn">
                {lookupLoading ? 'Searching...' : 'Search'}
              </Button>
            </div>
            
            {/* Lookup Result */}
            {lookupResult && (
              <div className="mt-4 p-4 border rounded-lg bg-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-bold">{lookupResult.gate_pass_number}</h3>
                    <p className="text-muted-foreground">{lookupResult.party_name}</p>
                  </div>
                  <Badge 
                    variant={lookupResult.status === 'pending_out' ? 'default' : 'secondary'}
                    className={lookupResult.status === 'pending_out' ? 'bg-orange-500' : ''}
                  >
                    {lookupResult.status_label}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">DC Number:</span>
                    <span className="ml-2 font-medium">{lookupResult.dc_number || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>
                    <span className="ml-2 font-medium capitalize">{lookupResult.category?.replace('_', ' ') || '-'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Vehicle:</span>
                    <span className="ml-2 font-medium">{lookupResult.vehicle_number || 'Not assigned'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Returnable:</span>
                    <span className="ml-2 font-medium">{lookupResult.requires_return ? 'Yes' : 'No'}</span>
                  </div>
                </div>
                
                {lookupResult.items_summary?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Items:</p>
                    <ul className="text-sm space-y-1">
                      {lookupResult.items_summary.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <Package className="w-3 h-3 text-muted-foreground" />
                          {item.item_name} - {item.quantity} {item.unit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {lookupResult.can_mark_out && (
                  <Button 
                    className="w-full mt-4 bg-green-600 hover:bg-green-700"
                    onClick={() => openMarkOutDialog(lookupResult)}
                    data-testid="mark-out-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Mark as OUT
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending OUT ({pendingPasses.length})
            </TabsTrigger>
            <TabsTrigger value="in-transit" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              In Transit ({inTransitPasses.length})
            </TabsTrigger>
            <TabsTrigger value="pending-in" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Pending IN ({pendingInPasses.length})
            </TabsTrigger>
            <TabsTrigger value="today" className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Today's OUT ({todayOutPasses.length})
            </TabsTrigger>
            <TabsTrigger value="today-in" className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Today's IN ({todayInPasses.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Pending OUT Tab */}
          <TabsContent value="pending">
            <Card>
              <CardContent className="pt-4">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : pendingPasses.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-2" />
                    <p className="text-muted-foreground">No pending gate passes</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gate Pass #</TableHead>
                        <TableHead>Party</TableHead>
                        <TableHead>DC #</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingPasses.map((gp) => (
                        <TableRow key={gp.id}>
                          <TableCell className="font-mono font-bold">{gp.gate_pass_number}</TableCell>
                          <TableCell>{gp.party_name}</TableCell>
                          <TableCell>{gp.dc_number || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{gp.items_count} items</Badge>
                            {gp.requires_return && (
                              <Badge variant="outline" className="ml-1 text-orange-600">Returnable</Badge>
                            )}
                          </TableCell>
                          <TableCell>{gp.vehicle_number || '-'}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              size="sm" 
                              onClick={() => openMarkOutDialog(gp)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <LogOut className="w-4 h-4 mr-1" />
                              Mark OUT
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* In Transit Tab (sent from this location, awaiting Gate IN) */}
          <TabsContent value="in-transit">
            <Card>
              <CardContent className="pt-4">
                {inTransitPasses.length === 0 ? (
                  <div className="text-center py-8">
                    <Truck className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No materials in transit</p>
                    <p className="text-xs text-muted-foreground mt-1">Materials sent from this unit will appear here until received</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gate Pass #</TableHead>
                        <TableHead>To</TableHead>
                        <TableHead>IUT #</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Driver</TableHead>
                        <TableHead>OUT Time</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inTransitPasses.map((gp) => (
                        <TableRow key={gp.id}>
                          <TableCell className="font-mono font-bold">{gp.gate_pass_number}</TableCell>
                          <TableCell>{gp.to_location}</TableCell>
                          <TableCell>{gp.source_number || '-'}</TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openItemsDialog(gp)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View {gp.items?.length || 0} items
                            </Button>
                          </TableCell>
                          <TableCell className="font-mono">{gp.vehicle_number}</TableCell>
                          <TableCell>{gp.driver_name || '-'}</TableCell>
                          <TableCell>{formatTime(gp.out_at)}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openEditTransitDialog(gp)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => openResetConfirm(gp)}
                              className="text-red-600 hover:text-red-700"
                            >
                              Reset
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Today's OUT Tab */}
          <TabsContent value="today">
            <Card>
              <CardContent className="pt-4">
                {todayOutPasses.length === 0 ? (
                  <div className="text-center py-8">
                    <Truck className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No gate passes marked OUT today</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gate Pass #</TableHead>
                        <TableHead>Party</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Driver</TableHead>
                        <TableHead>OUT Time</TableHead>
                        <TableHead>Marked By</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {todayOutPasses.map((gp) => (
                        <TableRow key={gp.id}>
                          <TableCell className="font-mono font-bold">{gp.gate_pass_number}</TableCell>
                          <TableCell>{gp.party_name}</TableCell>
                          <TableCell className="font-mono">{gp.vehicle_number}</TableCell>
                          <TableCell>{gp.driver_name || '-'}</TableCell>
                          <TableCell>{formatTime(gp.out_at)}</TableCell>
                          <TableCell>{gp.out_by_name}</TableCell>
                          <TableCell>
                            {gp.requires_return ? (
                              <Badge variant="outline" className="text-orange-600">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Pending Return
                              </Badge>
                            ) : (
                              <Badge className="bg-green-600">Completed</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending IN Tab (IUT arrivals at this location) */}
          <TabsContent value="pending-in">
            <Card>
              <CardContent className="pt-4">
                {pendingInPasses.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-2" />
                    <p className="text-muted-foreground">No pending arrivals</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gate Pass #</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>IUT #</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>OUT Time</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingInPasses.map((gp) => (
                        <TableRow key={gp.id}>
                          <TableCell className="font-mono font-bold">{gp.gate_pass_number}</TableCell>
                          <TableCell>{gp.from_location}</TableCell>
                          <TableCell>{gp.source_number || '-'}</TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openItemsDialog(gp)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View {gp.items?.length || 0} items
                            </Button>
                          </TableCell>
                          <TableCell className="font-mono">{gp.vehicle_number}</TableCell>
                          <TableCell>{formatTime(gp.out_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button 
                              size="sm" 
                              onClick={() => openMarkInDialog(gp)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Truck className="w-4 h-4 mr-1" />
                              Mark IN
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Today's IN Tab */}
          <TabsContent value="today-in">
            <Card>
              <CardContent className="pt-4">
                {todayInPasses.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No arrivals recorded today
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gate Pass #</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>OUT Time</TableHead>
                        <TableHead>IN Time</TableHead>
                        <TableHead>Marked By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {todayInPasses.map((gp) => (
                        <TableRow key={gp.id}>
                          <TableCell className="font-mono font-bold">{gp.gate_pass_number}</TableCell>
                          <TableCell>{gp.from_location}</TableCell>
                          <TableCell className="font-mono">{gp.vehicle_number}</TableCell>
                          <TableCell>{formatTime(gp.out_at)}</TableCell>
                          <TableCell>{formatTime(gp.in_at)}</TableCell>
                          <TableCell>{gp.in_by_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Mark OUT Dialog */}
      <Dialog open={markOutOpen} onOpenChange={setMarkOutOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="w-5 h-5" />
              Mark Gate Pass as OUT
            </DialogTitle>
            <DialogDescription>
              {selectedPass?.gate_pass_number} - {selectedPass?.party_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Items Section - Show what's going out */}
            {selectedPass?.items && selectedPass.items.length > 0 && (
              <div className="border rounded-lg p-3 bg-slate-50">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Items Going Out ({selectedPass.items.length})
                </h4>
                <div className="max-h-32 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead className="py-1">Item Name</TableHead>
                        <TableHead className="py-1 text-right">Qty</TableHead>
                        <TableHead className="py-1">Unit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPass.items.map((item, idx) => (
                        <TableRow key={idx} className="text-sm">
                          <TableCell className="py-1">{item.item_name}</TableCell>
                          <TableCell className="py-1 text-right font-medium">{item.quantity}</TableCell>
                          <TableCell className="py-1">{item.unit}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Vehicle Details */}
            <div className="space-y-2">
              <Label htmlFor="vehicle_number">Vehicle Number *</Label>
              <Input
                id="vehicle_number"
                value={markOutForm.vehicle_number}
                onChange={(e) => setMarkOutForm({ ...markOutForm, vehicle_number: e.target.value.toUpperCase() })}
                placeholder="e.g., MH12AB1234"
                className="uppercase"
                data-testid="vehicle-number-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="driver_name">Driver Name</Label>
                <Input
                  id="driver_name"
                  value={markOutForm.driver_name}
                  onChange={(e) => setMarkOutForm({ ...markOutForm, driver_name: e.target.value })}
                  placeholder="Driver's name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver_contact">Driver Contact</Label>
                <Input
                  id="driver_contact"
                  value={markOutForm.driver_contact}
                  onChange={(e) => setMarkOutForm({ ...markOutForm, driver_contact: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="security_name">Your Name (Security) *</Label>
              <Input
                id="security_name"
                value={markOutForm.security_name}
                onChange={(e) => setMarkOutForm({ ...markOutForm, security_name: e.target.value })}
                placeholder="Enter your name"
                data-testid="security-name-input"
                className={checkedInSecurity?.name ? 'bg-green-50 border-green-200' : ''}
              />
              {checkedInSecurity?.name && (
                <p className="text-xs text-green-600">Auto-filled from today's check-in</p>
              )}
              {!checkedInSecurity?.name && (
                <p className="text-xs text-muted-foreground">This will be recorded for audit trail</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="remarks">Remarks (Optional)</Label>
              <Textarea
                id="remarks"
                value={markOutForm.remarks}
                onChange={(e) => setMarkOutForm({ ...markOutForm, remarks: e.target.value })}
                placeholder="Any observations or notes..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkOutOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkOut} 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-mark-out-btn"
            >
              {submitting ? 'Processing...' : 'Confirm OUT'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark IN Dialog (for IUT arrivals) */}
      <Dialog open={markInOpen} onOpenChange={setMarkInOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Mark Gate Pass as IN
            </DialogTitle>
            <DialogDescription>
              {selectedInPass?.gate_pass_number} - Arriving from {selectedInPass?.from_location}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Transfer Details */}
            <div className="border rounded-lg p-3 bg-slate-50">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">IUT #:</span>
                  <span className="ml-2 font-medium">{selectedInPass?.source_number || '-'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Vehicle:</span>
                  <span className="ml-2 font-mono font-medium">{selectedInPass?.vehicle_number}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Driver:</span>
                  <span className="ml-2">{selectedInPass?.driver_name || '-'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Items:</span>
                  <span className="ml-2">{selectedInPass?.total_quantity || 0} qty</span>
                </div>
              </div>
            </div>

            {/* Security Name */}
            <div className="space-y-2">
              <Label htmlFor="in_security_name">Your Name (Security) <span className="text-red-500">*</span></Label>
              <Input
                id="in_security_name"
                value={markInForm.security_name}
                onChange={(e) => setMarkInForm({ ...markInForm, security_name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="in_remarks">Remarks (Optional)</Label>
              <Textarea
                id="in_remarks"
                value={markInForm.remarks}
                onChange={(e) => setMarkInForm({ ...markInForm, remarks: e.target.value })}
                placeholder="Any observations on arrival..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkInOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkIn} 
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? 'Processing...' : 'Confirm IN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Items View Dialog (Format C) */}
      <Dialog open={itemsDialogOpen} onOpenChange={setItemsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Items - {itemsDialogData?.gate_pass_number}
            </DialogTitle>
            <DialogDescription>
              {itemsDialogData?.from_location} → {itemsDialogData?.to_location}
            </DialogDescription>
          </DialogHeader>
          
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Item Name</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Unit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itemsDialogData?.items?.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{item.item_name}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-100 font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right">
                    {itemsDialogData?.items?.reduce((sum, item) => sum + (item.quantity || 0), 0)}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit In Transit Dialog */}
      <Dialog open={editTransitOpen} onOpenChange={setEditTransitOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Edit In-Transit Gate Pass
            </DialogTitle>
            <DialogDescription>
              {editTransitPass?.gate_pass_number} - To {editTransitPass?.to_location}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_vehicle">Vehicle Number</Label>
              <Input
                id="edit_vehicle"
                value={editTransitForm.vehicle_number}
                onChange={(e) => setEditTransitForm({ ...editTransitForm, vehicle_number: e.target.value.toUpperCase() })}
                placeholder="e.g., MH12AB1234"
                className="uppercase"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_driver_name">Driver Name</Label>
                <Input
                  id="edit_driver_name"
                  value={editTransitForm.driver_name}
                  onChange={(e) => setEditTransitForm({ ...editTransitForm, driver_name: e.target.value })}
                  placeholder="Driver's name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_driver_contact">Driver Contact</Label>
                <Input
                  id="edit_driver_contact"
                  value={editTransitForm.driver_contact}
                  onChange={(e) => setEditTransitForm({ ...editTransitForm, driver_contact: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_remarks">Remarks</Label>
              <Textarea
                id="edit_remarks"
                value={editTransitForm.remarks}
                onChange={(e) => setEditTransitForm({ ...editTransitForm, remarks: e.target.value })}
                placeholder="Any notes..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit_security_name">Your Name (Security) <span className="text-red-500">*</span></Label>
              <Input
                id="edit_security_name"
                value={editTransitForm.security_name}
                onChange={(e) => setEditTransitForm({ ...editTransitForm, security_name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTransitOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditTransit} 
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Reset Gate Pass?
            </DialogTitle>
            <DialogDescription>
              This will reset <strong>{resetPass?.gate_pass_number}</strong> back to "Pending OUT" status.
              The dispatch details (vehicle, driver, OUT time) will be cleared.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border rounded-lg p-3 bg-red-50 text-sm">
              <p><strong>Current Status:</strong> In Transit to {resetPass?.to_location}</p>
              <p><strong>Vehicle:</strong> {resetPass?.vehicle_number}</p>
              <p><strong>OUT Time:</strong> {formatTime(resetPass?.out_at)}</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reset_reason">Reason for Reset (Optional)</Label>
              <Textarea
                id="reset_reason"
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                placeholder="e.g., Wrong vehicle assigned, items need to be rechecked..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetTransit} 
              disabled={submitting}
              variant="destructive"
            >
              {submitting ? 'Resetting...' : 'Confirm Reset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
